import React, { useState } from "react";
import { MdTranslate } from "react-icons/md";
import {
  TRANSLATE_ERROR_ANONYMITY,
  TRANSLATE_ERROR_DISABLED,
  TRANSLATE_ERROR_GENERIC,
  TRANSLATE_MODE_ENGLISH,
  TRANSLATE_MODE_ORIGINAL,
  TRANSLATE_NOTICE_BUSY,
  TRANSLATE_NOTICE_RATE_LIMITED,
  TRANSLATE_STATUS_DONE,
  TRANSLATE_STATUS_RUNNING,
} from "../../../../utils/translateResponsesCore";
import {
  TRANSLATE_LABEL,
  TRANSLATE_MESSAGE_ANONYMITY,
  TRANSLATE_MESSAGE_BUSY,
  TRANSLATE_MESSAGE_DISABLED,
  TRANSLATE_MESSAGE_ERROR,
  TRANSLATE_MESSAGE_RATE_LIMITED,
  TRANSLATE_MESSAGE_UNAVAILABLE,
  TRANSLATE_OPTION_ENGLISH,
  TRANSLATE_OPTION_ORIGINAL,
  TRANSLATE_RETRY_LABEL,
  buildTranslateProgress,
  buildTranslateSummary,
} from "../../../../utils/triviaResultsStrings";
import "./TranslateToggle.css";

let translateToggleInstances = 0;

const STATUS_TEXT_CLASS = "translate-toggle-status-text";

// Shared By User "Translate" card; `translation` is the useTranslatedResponses
// return value, so Messages and Survey/KC render the exact same control.
// `action` (the Export CSV button) sits right of the card, status text under it.
// `hideProgress`: no open-text answers to count, so only errors show under the card.
const TranslateToggle = ({ translation, action, hideProgress }) => {
  const [uid] = useState(() => {
    translateToggleInstances += 1;
    return translateToggleInstances;
  });
  const { mode, setMode, status, progress, error, retry } = translation;
  const labelId = `translate-toggle-label-${uid}`;
  const groupName = `translate-toggle-mode-${uid}`;

  const renderStatus = () => {
    if (error) {
      if (error.kind === TRANSLATE_ERROR_GENERIC) {
        return (
          <p className={`${STATUS_TEXT_CLASS} text-danger`}>
            {TRANSLATE_MESSAGE_ERROR}
            <button type="button" className="translate-toggle-retry" onClick={retry}>
              {TRANSLATE_RETRY_LABEL}
            </button>
          </p>
        );
      }
      let message = TRANSLATE_MESSAGE_UNAVAILABLE;
      if (error.kind === TRANSLATE_ERROR_ANONYMITY) {
        message = TRANSLATE_MESSAGE_ANONYMITY;
      } else if (error.kind === TRANSLATE_ERROR_DISABLED) {
        message = TRANSLATE_MESSAGE_DISABLED;
      }
      return <p className={STATUS_TEXT_CLASS}>{message}</p>;
    }
    if (hideProgress) {
      return null;
    }
    if (status === TRANSLATE_STATUS_RUNNING) {
      if (progress.notice === TRANSLATE_NOTICE_BUSY) {
        return <p className={STATUS_TEXT_CLASS}>{TRANSLATE_MESSAGE_BUSY}</p>;
      }
      if (progress.notice === TRANSLATE_NOTICE_RATE_LIMITED) {
        return <p className={STATUS_TEXT_CLASS}>{TRANSLATE_MESSAGE_RATE_LIMITED}</p>;
      }
      return (
        <p className={STATUS_TEXT_CLASS}>
          {buildTranslateProgress(progress.checked, null)}
        </p>
      );
    }
    if (status === TRANSLATE_STATUS_DONE) {
      return (
        <p className={STATUS_TEXT_CLASS}>
          {buildTranslateSummary(
            progress.translated,
            progress.failed,
            progress.pending
          )}
        </p>
      );
    }
    return null;
  };

  const renderOption = (value, label) => {
    const checked = mode === value;
    return (
      <label className={`translate-toggle-option${checked ? " is-checked" : ""}`}>
        <input
          type="radio"
          name={groupName}
          value={value}
          checked={checked}
          onChange={() => setMode(value)}
        />
        {label}
      </label>
    );
  };

  return (
    <div className={`translate-toggle${action ? " has-action" : ""}`}>
      <div className="translate-toggle-card">
        <div id={labelId} className="translate-toggle-eyebrow">
          <MdTranslate className="translate-toggle-icon" aria-hidden="true" />
          {TRANSLATE_LABEL}
        </div>
        <div
          role="radiogroup"
          aria-labelledby={labelId}
          className="translate-toggle-options"
        >
          {renderOption(TRANSLATE_MODE_ORIGINAL, TRANSLATE_OPTION_ORIGINAL)}
          {renderOption(TRANSLATE_MODE_ENGLISH, TRANSLATE_OPTION_ENGLISH)}
        </div>
      </div>
      {action && <div className="translate-toggle-action">{action}</div>}
      <div className="translate-toggle-status" aria-live="polite">
        {renderStatus()}
      </div>
    </div>
  );
};

export default TranslateToggle;
